import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CropperJS from 'react-cropper';
import styles from './CropWorkspace.module.scss';
import 'cropperjs/dist/cropper.css';

const UNSUPPORTED_FILE_ERROR =
  'Unsupported file type. Please choose a JPG or PNG.';

function isAcceptedImageFile(file) {
  if (!file) {
    return false;
  }
  const type = (file.type || '').toLowerCase();
  if (type === 'image/jpeg' || type === 'image/png') {
    return true;
  }
  // Reject non-empty MIME types that aren't JPG/PNG before checking the name.
  if (type) {
    return false;
  }
  // Some browsers omit MIME type on drop — fall back to extension.
  const name = (file.name || '').toLowerCase();
  return /\.(jpe?g|png)$/.test(name);
}

/**
 * Shared crop workspace used by Adjust thumbnail, Profile picture, and Cover photo.
 * Configure via `maskShape` (`rect` | `circle`) and `aspectRatio` — not separate forks.
 */
const CropWorkspace = ({
  src,
  cropperRef,
  aspectRatio,
  maskShape,
  wide,
  cropBoxResizable,
  onReady,
  onCrop,
  onZoom,
  zoom,
  minZoom,
  maxZoom,
  onZoomChange,
  showZoom,
  hint,
  repositionHint,
  emptyTitle,
  emptyHint,
  emptyIcon,
  onEmptyClick,
  fileInputRef,
  onFileChange,
  accept,
  onReset,
}) => {
  const [pillHidden, setPillHidden] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);
  const pillTimerRef = useRef(null);

  useEffect(() => {
    setPillHidden(false);
    if (pillTimerRef.current) {
      clearTimeout(pillTimerRef.current);
    }
    if (src && repositionHint) {
      pillTimerRef.current = setTimeout(() => setPillHidden(true), 2800);
    }
    return () => {
      if (pillTimerRef.current) {
        clearTimeout(pillTimerRef.current);
      }
    };
  }, [src, repositionHint]);

  useEffect(() => {
    if (src) {
      setFileError(null);
    }
  }, [src]);

  const handleCrop = (event) => {
    if (repositionHint && !pillHidden) {
      setPillHidden(true);
    }
    if (onCrop) {
      onCrop(event);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file =
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0];
    if (!file || !onFileChange) {
      return;
    }
    if (!isAcceptedImageFile(file)) {
      setFileError(UNSUPPORTED_FILE_ERROR);
      return;
    }
    setFileError(null);
    // Synthesize a change-like event for the shared file handler.
    onFileChange({ target: { files: [file], value: '' } });
  };

  const emptyTitleNode = (() => {
    if (!emptyTitle) {
      return null;
    }
    const parts = emptyTitle.split(/\bbrowse\b/i);
    if (parts.length < 2) {
      return <p className={styles.emptyTitle}>{emptyTitle}</p>;
    }
    return (
      <p className={styles.emptyTitle}>
        {parts[0]}
        <span className={styles.browseLink}>browse</span>
        {parts.slice(1).join('browse')}
      </p>
    );
  })();

  return (
    <div>
      <div
        className={
          src
            ? `${styles.surface}${wide ? ` ${styles.wide}` : ''}`
            : `${styles.emptyDropzone}${
                emptyIcon ? ` ${styles.emptyDropzoneCircle}` : ''
              }${dragOver ? ` ${styles.dragOver}` : ''}`
        }
        role={!src ? 'button' : undefined}
        tabIndex={!src ? 0 : undefined}
        onClick={!src ? onEmptyClick : undefined}
        onKeyDown={
          !src
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (onEmptyClick) {
                    onEmptyClick();
                  }
                }
              }
            : undefined
        }
        onDragOver={(event) => {
          event.preventDefault();
          if (!src) {
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {!src ? (
          <>
            {emptyIcon ? (
              <div className={styles.emptyAvatar} aria-hidden="true">
                {emptyIcon}
              </div>
            ) : null}
            {emptyTitleNode}
            {fileError ? (
              <p className={styles.fileError} role="alert">
                {fileError}
              </p>
            ) : emptyHint ? (
              <p className={styles.emptyHint}>{emptyHint}</p>
            ) : null}
          </>
        ) : (
          <>
            <div
              className={`${styles.cropper}${
                maskShape === 'circle' ? ` ${styles.circle}` : ''
              }`}
            >
              <CropperJS
                key={typeof src === 'string' ? src.slice(0, 64) : 'crop'}
                ref={cropperRef}
                src={src}
                style={{ width: '100%', height: '100%' }}
                aspectRatio={aspectRatio}
                viewMode={1}
                dragMode="move"
                autoCropArea={1}
                background={false}
                guides={false}
                center={false}
                highlight={false}
                cropBoxMovable={false}
                cropBoxResizable={
                  maskShape === 'circle' ? false : Boolean(cropBoxResizable)
                }
                toggleDragModeOnDblclick={false}
                checkCrossOrigin={false}
                zoomOnWheel
                zoomOnTouch
                ready={onReady}
                crop={handleCrop}
                zoom={onZoom}
              />
            </div>
            {fileError ? (
              <p
                className={`${styles.fileError} ${styles.fileErrorOnCrop}`}
                role="alert"
              >
                {fileError}
              </p>
            ) : null}
          </>
        )}
        {src && repositionHint ? (
          <div
            className={`${styles.repositionPill}${
              pillHidden ? ` ${styles.hidden}` : ''
            }`}
            aria-hidden="true"
          >
            {repositionHint}
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          className={styles.fileInput}
          type="file"
          accept={accept}
          onChange={onFileChange}
        />
      </div>

      {showZoom ? (
        <>
          <div className={styles.zoomRow}>
            <span className={styles.zoomLabel} id="crop-workspace-zoom-label">
              Zoom
            </span>
            <input
              className={styles.zoomSlider}
              type="range"
              min={minZoom}
              max={maxZoom}
              step={(maxZoom - minZoom) / 100 || 0.01}
              value={Math.min(maxZoom, Math.max(minZoom, zoom))}
              onChange={onZoomChange}
              disabled={!src}
              aria-labelledby="crop-workspace-zoom-label"
            />
            {onReset ? (
              <button
                type="button"
                className={styles.resetButton}
                onClick={onReset}
                disabled={!src}
              >
                Reset
              </button>
            ) : null}
          </div>
          {hint ? <p className={styles.hint}>{hint}</p> : null}
        </>
      ) : null}
    </div>
  );
};

CropWorkspace.propTypes = {
  src: PropTypes.string,
  cropperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  aspectRatio: PropTypes.number.isRequired,
  maskShape: PropTypes.oneOf(['rect', 'circle']),
  wide: PropTypes.bool,
  cropBoxResizable: PropTypes.bool,
  onReady: PropTypes.func,
  onCrop: PropTypes.func,
  onZoom: PropTypes.func,
  zoom: PropTypes.number,
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  onZoomChange: PropTypes.func,
  showZoom: PropTypes.bool,
  hint: PropTypes.string,
  repositionHint: PropTypes.string,
  emptyTitle: PropTypes.string,
  emptyHint: PropTypes.string,
  emptyIcon: PropTypes.node,
  onEmptyClick: PropTypes.func,
  fileInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onFileChange: PropTypes.func,
  accept: PropTypes.string,
  onReset: PropTypes.func,
};

CropWorkspace.defaultProps = {
  src: null,
  maskShape: 'rect',
  wide: false,
  cropBoxResizable: true,
  zoom: 1,
  minZoom: 0.1,
  maxZoom: 3,
  showZoom: true,
  hint: 'Drag to reposition · pinch or scroll to zoom',
  repositionHint: null,
  emptyTitle: 'Drop a photo here or browse',
  emptyHint: 'JPG or PNG',
  emptyIcon: null,
  accept: '.png,.jpeg,.jpg,image/png,image/jpeg',
  onReset: null,
};

export default CropWorkspace;
